import { z } from "zod";

/**
 * Shared DTO cho Admin per-group AI enable/disable toggle (US-E11.F1-0064 · @UC0071 · E13/E14 AI-ops).
 * Một nguồn sự thật cho api (controller + DTO — guard + validate biên PATCH) và admin web (render/sửa).
 *
 * Ghi `Group.settings.ai_enabled` (jsonb override mức nhóm) — read side đã có (AiEnabledService.
 * isAiEnabled). Đây là WRITE path admin bổ sung (trước đó chỉ đọc, chưa có surface ghi).
 *
 * BẤT BIẾN (invariants):
 * - Kill-switch toàn cục: config env `AI_ENABLED=false` ⇒ AI luôn tắt bất kể toggle nhóm. Toggle admin
 *   CHỈ tắt một nhóm khi global on; KHÔNG bao giờ ép bật khi global off (đọc `AiEnabledService`).
 * - `.strict()` — chặn key lạ tại biên (fail-closed input · C5). Body chỉ 1 field `enabled: boolean`.
 * - DTO chỉ mang trạng thái cờ — KHÔNG secret/token (C2/C3).
 * - `groupId` trên response là UUID string (khớp Group.id). `aiEnabled` = trạng thái hiệu lực đã tính
 *   (đã áp kill-switch global) — UI hiển thị đúng "AI có đang bật cho nhóm này không".
 */

/** Body PATCH /v1/admin/groups/:groupId/ai-config — bật/tắt AI cho một nhóm. Chỉ 1 field, strict. */
export const patchAdminAiConfigSchema = z
  .object({
    enabled: z.boolean(),
  })
  .strict();

export type PatchAdminAiConfigRequest = z.infer<typeof patchAdminAiConfigSchema>;

/**
 * Response GET/PATCH /v1/admin/groups/:groupId/ai-config — trạng thái hiệu lực AI của nhóm.
 * `aiEnabled` = kết quả `isAiEnabled(groupId)` (đã áp kill-switch global). `groupId` echo lại.
 */
export const adminAiConfigResponseSchema = z
  .object({
    groupId: z.string(),
    aiEnabled: z.boolean(),
  })
  .strict();

export type AdminAiConfigResponse = z.infer<typeof adminAiConfigResponseSchema>;
